import { inject, Injectable } from '@angular/core';
import { CodeAndName } from '@lx/shared/types';
import { sortArray } from '@lx/shared/utils-array';
import { ResidenceCountry, ResidenceCountryService } from '@lx/shared/data-access/destination';
import { getFormattedTimezoneOffset, normalizeString } from '@lx/shared/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { TIMEZONES, Timezone } from '@lx/quote-tool/shared/types';
import { getBrowserTimeZone } from '@lx/quote-tool/shared/utils/functions';
import { getTimezoneOffset } from 'date-fns-tz';

@Injectable()
export class TimezoneService {
  private timeZones = inject(TIMEZONES);
  timezones$: Observable<CodeAndName[]> = combineLatest([
    this.residenceCountryService.getAll().pipe(map((countries) => countries.map(({ code, name }) => ({ code, name })))),
    of(this.timeZones),
  ]).pipe(
    map(([countries, timezones]) => {
      return Object.entries(timezones).flatMap(([countryCode, zoneEntries]) => {
        const country = countries.find((c) => c.code === countryCode);
        return this.mapToTimezones(zoneEntries, country);
      });
    }),
    map((timezones) => sortArray(timezones, [{ property: 'name' }])),
    shareReplay(1),
  );

  constructor(private readonly residenceCountryService: ResidenceCountryService) {}

  /**
   * Builds a Timezone object from the browser's detected timezone
   * @param timezones - Array of available timezones to find metadata
   * @returns Timezone object with offset information
   */
  buildTimezoneFromBrowser(timezones: CodeAndName[]): Timezone {
    const timezone = getBrowserTimeZone();
    return this.buildTimezoneFromCode(timezone, timezones);
  }

  /**
   * Builds a Timezone object from a timezone code or CodeAndName object
   * @param timezoneInput - Either a timezone code string or a CodeAndName object
   * @param timezones - Array of available timezones to find metadata (optional if timezoneInput is CodeAndName)
   * @returns Timezone object with offset information
   */
  buildTimezoneFromCode(timezoneInput: string | CodeAndName, timezones?: CodeAndName[]): Timezone {
    let timezoneCode: string;
    let timeZoneInfo: CodeAndName | undefined;

    if (typeof timezoneInput === 'string') {
      timezoneCode = timezoneInput;
      timeZoneInfo = timezones?.find((tz) => normalizeString(tz.code) === normalizeString(timezoneCode));
    } else {
      timezoneCode = timezoneInput.code;
      timeZoneInfo = timezoneInput;
    }

    return {
      ...timeZoneInfo,
      timezone: timezoneCode,
      timezoneOffset: getTimezoneOffset(timezoneCode),
      formattedTimezoneOffset: getFormattedTimezoneOffset(timezoneCode),
    };
  }

  private mapToTimezones(entries: string[], country: CodeAndName): CodeAndName[] {
    return entries.map((timezone) => ({
      code: timezone,
      name: [...this.getTimezoneCountryCityNames(country, timezone), `(${getFormattedTimezoneOffset(timezone)})`].join(' - '),
    }));
  }

  private getTimezoneCountryCityNames(country: ResidenceCountry | undefined, timezone: string) {
    const parts: string[] = [];
    if (country) {
      parts.push(country.name);
    }
    const timezoneParts = timezone.split('/');
    parts.push(timezoneParts[timezoneParts.length - 1].replace(/_/, ' '));
    return parts;
  }
}
